/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { hearingsApi, mattersApi, utilitiesApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { CaretLeft, CaretRight, Gavel, Plus, WarningOctagon, CalendarBlank, Sparkle, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import HelpButton from "@/components/HelpButton";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function fmtDate(d) { return d.toISOString().slice(0, 10); }

export default function CalendarPage() {
    const [cursor, setCursor] = useState(new Date());
    const [hearings, setHearings] = useState([]);
    const [matters, setMatters] = useState([]);
    const [open, setOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);

    const monthStart = startOfMonth(cursor);
    const monthEnd = endOfMonth(cursor);

    const load = async () => {
        try {
            const arr = await hearingsApi.list({ from_: fmtDate(monthStart), to: fmtDate(monthEnd) });
            setHearings(arr);
        } catch {
            toast.error("Failed to load hearings");
        }
    };
    useEffect(() => { load(); }, [cursor]);
    useEffect(() => { mattersApi.list().then(setMatters).catch(() => {}); }, []);

    const byDate = useMemo(() => {
        const map = {};
        for (const h of hearings) {
            const k = (h.scheduled_at || "").slice(0, 10);
            if (!map[k]) map[k] = [];
            map[k].push(h);
        }
        return map;
    }, [hearings]);

    const cells = useMemo(() => {
        const first = monthStart;
        const last = monthEnd;
        const firstWeekday = (first.getDay() + 6) % 7; // make Mon=0
        const days = [];
        for (let i = 0; i < firstWeekday; i++) {
            const d = new Date(first);
            d.setDate(d.getDate() - (firstWeekday - i));
            days.push({ date: d, otherMonth: true });
        }
        for (let i = 1; i <= last.getDate(); i++) {
            days.push({ date: new Date(first.getFullYear(), first.getMonth(), i), otherMonth: false });
        }
        while (days.length % 7 !== 0) {
            const last = days[days.length - 1].date;
            const d = new Date(last); d.setDate(d.getDate() + 1);
            days.push({ date: d, otherMonth: true });
        }
        return days;
    }, [cursor]);

    const todayStr = fmtDate(new Date());

    return (
        <div className="p-4 md:p-8 max-w-[1400px] mx-auto" data-testid="calendar-page">
            <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
                <div className="rule-line pt-3">
                    <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-1">Court diary</div>
                <div className="flex items-center gap-2">
                    <h1 className="font-serif text-4xl tracking-tight">Calendar</h1>
                    <HelpButton topic="calendar" testid="help-calendar" />
                </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="icon" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} data-testid="cal-prev">
                        <CaretLeft size={14} />
                    </Button>
                    <div className="font-serif text-xl px-2 min-w-[180px] text-center" data-testid="cal-month">
                        {cursor.toLocaleString("en-IN", { month: "long", year: "numeric" })}
                    </div>
                    <Button variant="outline" size="icon" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} data-testid="cal-next">
                        <CaretRight size={14} />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setCursor(new Date())} data-testid="cal-today">Today</Button>
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button data-testid="new-hearing-btn"><Plus size={14} /> New hearing</Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-xl">
                            <DialogHeader>
                                <DialogTitle>New hearing</DialogTitle>
                                <DialogDescription>Adding to your court diary. Conflict check runs automatically against other hearings within ±2 hours.</DialogDescription>
                            </DialogHeader>
                            <HearingForm
                                matters={matters}
                                defaultDate={selectedDate}
                                onDone={(h) => { setOpen(false); setSelectedDate(null); setHearings((arr) => [...arr, h].sort((a, b) => a.scheduled_at < b.scheduled_at ? -1 : 1)); }}
                            />
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            <div className="border border-border rounded-md bg-white overflow-hidden" data-testid="calendar-grid">
                <div className="grid grid-cols-7 bg-muted text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    {DAY_LABELS.map((d) => <div key={d} className="p-2 text-center">{d}</div>)}
                </div>
                <div className="grid grid-cols-7">
                    {cells.map((c, idx) => {
                        const k = fmtDate(c.date);
                        const list = byDate[k] || [];
                        return (
                            <div
                                key={idx}
                                className={`min-h-[100px] border-t border-r border-border p-1.5 cursor-pointer transition-colors ${
                                    c.otherMonth ? "bg-muted/30 text-muted-foreground/50" : "bg-white hover:bg-amber-50/30"
                                } ${k === todayStr ? "ring-1 ring-amber-400 ring-inset" : ""}`}
                                onClick={() => { setSelectedDate(k); setOpen(true); }}
                                data-testid={`cal-cell-${k}`}
                            >
                                <div className={`text-xs font-medium ${k === todayStr ? "text-amber-700" : ""}`}>{c.date.getDate()}</div>
                                <div className="space-y-0.5 mt-1">
                                    {list.slice(0, 3).map((h) => (
                                        <Link
                                            key={h.id}
                                            to={`/matters/${h.matter_id}`}
                                            onClick={(e) => e.stopPropagation()}
                                            className={`block text-[10px] leading-tight truncate px-1.5 py-0.5 rounded-sm border ${
                                                h.status === "adjourned" ? "bg-yellow-100/60 border-yellow-300 text-yellow-900" :
                                                h.status === "cancelled" ? "bg-muted line-through" :
                                                h.status === "held" ? "bg-emerald-100/60 border-emerald-300 text-emerald-900" :
                                                "bg-amber-100/70 border-amber-300 text-amber-900"
                                            }`}
                                        >
                                            {(h.scheduled_at || "").slice(11, 16)} · {(h.matter || {}).title || h.matter_id}
                                        </Link>
                                    ))}
                                    {list.length > 3 && <div className="text-[10px] text-muted-foreground">+{list.length - 3} more</div>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Upcoming list */}
            <UpcomingHearings hearings={hearings} todayStr={todayStr} onReload={load} />
        </div>
    );
}

function UpcomingHearings({ hearings, todayStr, onReload }) {
    const upcoming = hearings.filter((h) => h.scheduled_at >= todayStr && h.status !== "cancelled").slice(0, 12);
    if (!upcoming.length) return null;
    return (
        <section className="mt-8" data-testid="upcoming-hearings">
            <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">Upcoming hearings</div>
            <ul className="border border-border bg-white rounded-md divide-y divide-border">
                {upcoming.map((h) => (
                    <li key={h.id} className="p-3 flex items-center gap-3 text-sm" data-testid={`upcoming-${h.id}`}>
                        <Gavel size={14} className="text-muted-foreground" />
                        <div className="flex-1 min-w-0">
                            <Link to={`/matters/${h.matter_id}`} className="font-medium hover:underline">
                                {(h.matter || {}).title || "—"}
                            </Link>
                            <div className="text-xs text-muted-foreground">
                                {new Date(h.scheduled_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                                {" · "}{h.court}
                                {h.courtroom ? ` · ${h.courtroom}` : ""}
                                {h.item_number ? ` · Item ${h.item_number}` : ""}
                            </div>
                        </div>
                        <PrepPackButton hearing={h} />
                        <HearingActions hearing={h} onChanged={onReload} />
                    </li>
                ))}
            </ul>
        </section>
    );
}

function PrepPackButton({ hearing }) {
    const [open, setOpen] = useState(false);
    const [content, setContent] = useState("");
    const [busy, setBusy] = useState(false);
    const generate = async () => {
        setBusy(true);
        try {
            const r = await utilitiesApi.hearingPrep({ hearing_id: hearing.id });
            setContent(r.markdown || "(empty)");
            setOpen(true);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Prep generation failed");
        }
        setBusy(false);
    };
    return (
        <>
            <Button variant="outline" size="sm" onClick={generate} disabled={busy} data-testid={`prep-pack-${hearing.id}`}>
                <Sparkle size={12} weight="fill" /> {busy ? "…" : "Prep pack"}
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Hearing prep pack</DialogTitle>
                        <DialogDescription>AI-drafted briefing note — review before relying on it in court.</DialogDescription>
                    </DialogHeader>
                    <article className="legal-rendered" data-testid="prep-pack-content">
                        <pre className="whitespace-pre-wrap font-serif text-[15px]">{content}</pre>
                    </article>
                </DialogContent>
            </Dialog>
        </>
    );
}

function HearingActions({ hearing, onChanged }) {
    const [open, setOpen] = useState(false);
    const [v, setV] = useState({
        status: hearing.status, outcome: hearing.outcome || "", order_summary: hearing.order_summary || "",
        next_date: hearing.next_date || "", next_purpose: hearing.next_purpose || "",
    });
    const save = async () => {
        try {
            await hearingsApi.update(hearing.id, v);
            toast.success("Hearing updated");
            setOpen(false);
            onChanged?.();
        } catch {
            toast.error("Save failed");
        }
    };
    return (
        <>
            <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid={`hearing-update-${hearing.id}`}>
                Update <ArrowRight size={12} />
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Update hearing</DialogTitle>
                        <DialogDescription>Mark held / adjourned / cancelled. Adjournment auto-creates the next hearing.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2">
                        <Labeled label="Status">
                            <Select value={v.status} onValueChange={(s) => setV({ ...v, status: s })}>
                                <SelectTrigger data-testid="hearing-status-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="scheduled">Scheduled</SelectItem>
                                    <SelectItem value="held">Held</SelectItem>
                                    <SelectItem value="adjourned">Adjourned</SelectItem>
                                    <SelectItem value="cancelled">Cancelled</SelectItem>
                                </SelectContent>
                            </Select>
                        </Labeled>
                        <Labeled label="Outcome / order summary">
                            <Textarea rows={3} value={v.order_summary} onChange={(e) => setV({ ...v, order_summary: e.target.value })} placeholder="e.g., 'Counter to be filed within 4 weeks; listed for arguments on 28-06-2026.'" />
                        </Labeled>
                        {v.status === "adjourned" && (
                            <>
                                <Labeled label="Next date (date or datetime)">
                                    <Input type="date" value={v.next_date.slice(0, 10)} onChange={(e) => setV({ ...v, next_date: e.target.value })} data-testid="hearing-next-date" />
                                </Labeled>
                                <Labeled label="Next purpose">
                                    <Input value={v.next_purpose} onChange={(e) => setV({ ...v, next_purpose: e.target.value })} placeholder="e.g., 'For arguments'" />
                                </Labeled>
                            </>
                        )}
                        <div className="flex justify-end">
                            <Button onClick={save} data-testid="hearing-save">Save</Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}

export function HearingForm({ matters, defaultDate, defaultMatterId, onDone }) {
    const today = new Date().toISOString().slice(0, 10);
    const [v, setV] = useState({
        matter_id: defaultMatterId || "",
        scheduled_date: defaultDate || today,
        scheduled_time: "10:30",
        court: "",
        courtroom: "",
        bench: "",
        item_number: "",
        purpose: "",
        stage: "",
    });
    const [conflicts, setConflicts] = useState([]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (defaultDate) setV((x) => ({ ...x, scheduled_date: defaultDate }));
    }, [defaultDate]);

    useEffect(() => {
        if (!v.matter_id) return;
        const m = matters.find((x) => x.id === v.matter_id);
        if (m && !v.court) setV((x) => ({ ...x, court: m.court || "" }));
    }, [v.matter_id, matters]);

    const checkConflicts = async () => {
        if (!v.scheduled_date || !v.scheduled_time) return;
        const at = `${v.scheduled_date}T${v.scheduled_time}:00`;
        try {
            const r = await hearingsApi.detectConflicts({ scheduled_at: at, court: v.court, matter_id: v.matter_id || null });
            setConflicts(r.conflicts || []);
        } catch {
            // silent
        }
    };

    const submit = async () => {
        if (!v.matter_id) { toast.error("Pick a matter"); return; }
        if (!v.court.trim()) { toast.error("Court required"); return; }
        const at = `${v.scheduled_date}T${v.scheduled_time}:00`;
        setBusy(true);
        try {
            const h = await hearingsApi.create({
                matter_id: v.matter_id, scheduled_at: at, court: v.court,
                courtroom: v.courtroom, bench: v.bench, item_number: v.item_number,
                purpose: v.purpose, stage: v.stage,
            });
            const matter = matters.find((m) => m.id === h.matter_id);
            h.matter = matter ? { id: matter.id, title: matter.title, case_number: matter.case_number } : null;
            toast.success("Hearing added");
            onDone?.(h);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to add hearing");
        }
        setBusy(false);
    };

    return (
        <div className="space-y-3" data-testid="hearing-form">
            <Labeled label="Matter">
                <Select value={v.matter_id || ""} onValueChange={(val) => setV({ ...v, matter_id: val })}>
                    <SelectTrigger data-testid="hearing-matter"><SelectValue placeholder="Pick a matter…" /></SelectTrigger>
                    <SelectContent>
                        {matters.map((m) => <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>)}
                    </SelectContent>
                </Select>
            </Labeled>
            <div className="grid grid-cols-2 gap-2">
                <Labeled label="Date">
                    <Input type="date" value={v.scheduled_date} onChange={(e) => setV({ ...v, scheduled_date: e.target.value })} onBlur={checkConflicts} data-testid="hearing-date" />
                </Labeled>
                <Labeled label="Time">
                    <Input type="time" value={v.scheduled_time} onChange={(e) => setV({ ...v, scheduled_time: e.target.value })} onBlur={checkConflicts} data-testid="hearing-time" />
                </Labeled>
            </div>
            <Labeled label="Court">
                <Input value={v.court} onChange={(e) => setV({ ...v, court: e.target.value })} onBlur={checkConflicts} placeholder="Delhi High Court" data-testid="hearing-court" />
            </Labeled>
            <div className="grid grid-cols-3 gap-2">
                <Labeled label="Courtroom">
                    <Input value={v.courtroom} onChange={(e) => setV({ ...v, courtroom: e.target.value })} placeholder="38" />
                </Labeled>
                <Labeled label="Bench">
                    <Input value={v.bench} onChange={(e) => setV({ ...v, bench: e.target.value })} placeholder="Hon'ble Mr. J. XYZ" />
                </Labeled>
                <Labeled label="Item">
                    <Input value={v.item_number} onChange={(e) => setV({ ...v, item_number: e.target.value })} placeholder="12" />
                </Labeled>
            </div>
            <Labeled label="Purpose">
                <Input value={v.purpose} onChange={(e) => setV({ ...v, purpose: e.target.value })} placeholder="For arguments" />
            </Labeled>

            {!!conflicts.length && (
                <div className="border border-destructive/40 bg-destructive/5 rounded-sm p-2 text-sm" data-testid="hearing-conflicts">
                    <div className="flex items-center gap-1 font-medium text-destructive mb-1"><WarningOctagon size={14} /> Conflict detected</div>
                    {conflicts.map((c, i) => <div key={i} className="text-xs">{c.message}</div>)}
                </div>
            )}

            <div className="flex justify-end pt-1">
                <Button onClick={submit} disabled={busy} data-testid="hearing-submit">{busy ? "Saving…" : "Add hearing"}</Button>
            </div>
        </div>
    );
}

function Labeled({ label, children }) {
    return (
        <div className="space-y-1">
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
            {children}
        </div>
    );
}
